import {
  ContractPartnerType,
  CurrentContractPartner,
  MeterPointChangeState,
  MeterPointChangeType,
  NewContractPartner,
  ProductAndDate,
} from '../../pages/meter-point-change-request/meterpoint-change-request.types';
import { Address } from '@mwe/models';
import { IAddress, IContractPartner, IMeterPointChangeRequest, IProductsAndDate } from '../../model/meterpoint-change-request-http.model';
import { ContactDataType } from '../../pages/meter-point-change-request/03_current_contract_partner/model/occupied_contract_partner.model';
import { getDayJsFromNgbDateStruct } from '@mwe/utils';
import { DATE_FORMAT_GERMAN } from '@shared-ikp/app.constants';
import { EmptyContractPartner } from '../../pages/meter-point-change-request/03_current_contract_partner/model/empty_contract_partner.model';
import { MeterPoint } from '../../model/meter-point.model';

export class MeterPointChangeTransformer {
  public static mapToIMeterPointChangeRequest(meterPointChangeRequest: MeterPointChangeState): IMeterPointChangeRequest {
    return {
      typeOfChange: meterPointChangeRequest.stepChangeType,
      productsAndDate: this.mapProductsAndDate(meterPointChangeRequest.productAndDate, meterPointChangeRequest.meterPoint.adresse),
      currentContractPartner: this.mapCurrentContractPartner(meterPointChangeRequest.currentContractPartner),
      newContractPartner: this.mapNewContractPartner(meterPointChangeRequest.newContractPartner),
      newInvoiceRecipient: this.mapNewInvoiceRecipient(meterPointChangeRequest.newContractPartner),
      currentInvoiceRecipient:
        meterPointChangeRequest.stepChangeType === MeterPointChangeType.EMPTY_TO_OCCUPIED
          ? this.mapCurrentInvoiceRecipient(meterPointChangeRequest.meterPoint)
          : null,
    };
  }

  private static mapProductsAndDate(productsAndDate: ProductAndDate, address: Address): IProductsAndDate {
    return {
      date: null,
      meterPointAddress: this.mapIAddress(address),
      meterPointSelection: productsAndDate.meterPoints.map(meterPoint => ({
        id: meterPoint.id,
        productCategory: meterPoint.category.toUpperCase(),
        meterPointNumber: meterPoint.meterPointNumber,
        meterNumber: meterPoint.meterNumber,
        meterCount: meterPoint.meterCount,
        contractNumber: meterPoint.contractNumber,
      })),
    };
  }

  private static mapCurrentContractPartner(contractPartner: CurrentContractPartner): IContractPartner {
    if (contractPartner?.emptyContractPartner) {
      return this.mapCurrentEmptyContractPartner(contractPartner.emptyContractPartner);
    }
    if (contractPartner?.occupiedContractPartner) {
      const info = contractPartner.occupiedContractPartner;
      if (info.isContractPartnerDataKnown === false) {
        return null;
      }
      return {
        contractPartnerType:
          info.organizationType === ContactDataType.PRIVATE_INDIVIDUAL ? ContractPartnerType.PRIVATE : ContractPartnerType.COMPANY,
        firstName: info.contactDataIndividual?.firstName,
        lastName: info.contactDataIndividual?.lastName,
        birthday: null,
        email: info.contactDataIndividual?.email ?? info.contactDataOrganization?.email,
        phoneNumber: info.contactDataIndividual?.phoneNumber ?? info.contactDataOrganization?.phoneNumber,
        organisationName: info.contactDataOrganization?.name,
        uid: info.contactDataOrganization?.uid,
        firmenbuchNr: info.contactDataOrganization?.firmenbuchNr,
        address: info.invoiceAddress,
      };
    }
    return null;
  }

  private static mapNewContractPartner(contractPartner: NewContractPartner): IContractPartner {
    if (contractPartner?.emptyPartner) {
      const partner = contractPartner.emptyPartner;
      return {
        contractPartnerType: ContractPartnerType.COMPANY,
        organisationName: partner.name,
        uid: partner.uid,
        email: partner.email,
        phoneNumber: partner.phone,
        gpNr: partner.gpNr,
        address: partner.address
          ? {
              plz: partner.address.plz,
              city: partner.address.city,
              street: partner.address.street,
              houseNumberStaircase: partner.address.houseNumberStaircase,
              door: partner.address.door,
            }
          : null,
      };
    }
    if (contractPartner?.formData) {
      const partnerInfo = contractPartner.formData;
      return {
        contractPartnerType: partnerInfo.partnerType,
        firstName: partnerInfo.privatePartner?.firstName,
        lastName: partnerInfo.privatePartner?.lastName,
        birthday: partnerInfo.privatePartner?.birthDate
          ? getDayJsFromNgbDateStruct(partnerInfo.privatePartner.birthDate).format(DATE_FORMAT_GERMAN)
          : null,
        email: partnerInfo.privatePartner?.email ?? partnerInfo.organizationPartner?.email,
        phoneNumber: partnerInfo.privatePartner?.phone ?? partnerInfo.organizationPartner?.phone,
        organisationName: partnerInfo.organizationPartner?.name,
        uid: partnerInfo.organizationPartner?.uidNumber,
        firmenbuchNr: partnerInfo.organizationPartner?.firmenbuchNumber,
        address: contractPartner.alternativeInvoicingAddress?.address,
      };
    }
    return null;
  }

  private static mapNewInvoiceRecipient(contractPartner: NewContractPartner): IContractPartner {
    if (contractPartner?.alternativeInvoicingAddress) {
      return {
        contractPartnerType: ContractPartnerType.COMPANY,
        organisationName: contractPartner.alternativeInvoicingAddress.company,
        uid: contractPartner.alternativeInvoicingAddress.uid,
        gpNr: contractPartner.alternativeInvoicingAddress.gpNr,
        address: contractPartner.alternativeInvoicingAddress.address,
      };
    }
    return null;
  }

  private static mapCurrentEmptyContractPartner(contractPartner: EmptyContractPartner): IContractPartner {
    return {
      contractPartnerType: ContractPartnerType.COMPANY,
      organisationName: contractPartner.company,
      address: contractPartner.invoiceAddress?.address ?? contractPartner.address,
    };
  }

  private static mapCurrentInvoiceRecipient(meterPoint: MeterPoint): IContractPartner | null {
    return meterPoint?.hasAbweichenderRechnungsempfaenger
      ? {
          contractPartnerType: ContractPartnerType.COMPANY,
          organisationName: meterPoint.rechnungsempfaengerName,
          uid: meterPoint.rechnungsempfaengerUid,
          gpNr: meterPoint.rechnungsempfaengerGpNr,
          address: meterPoint.rechnungsAdresse ? this.mapIAddress(meterPoint.rechnungsAdresse) : null,
        }
      : null;
  }

  private static mapIAddress(address: Address): IAddress {
    return {
      plz: address.postcode,
      city: address.city,
      street: address.street,
      houseNumberStaircase: address.streetNumber,
      door: address.doorNumber,
    };
  }
}
